import {
	Checkbox,
	FormControlLabel,
	Radio,
	RadioGroup,
	Switch,
	TextField,
	Typography
} from "@material-ui/core";
import React, { forwardRef, useImperativeHandle, useMemo } from "react";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Grid from "@material-ui/core/Grid";
import Toolbar from "@material-ui/core/Toolbar";
import Divider from "@material-ui/core/Divider";
import Box from "@material-ui/core/Box";
import { PrimaryButton, SecondaryButton } from "@prestosports/commons-ui";
import { useFormik } from "formik";
import { Autocomplete } from "@material-ui/lab";
import * as Yup from "yup";
import { SportUtils } from "@prestosports/prestostats-core/Common";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import configurationStyles from "../styles/Configuration.styles";
import commonStyles from "../styles/Common.styles";
import { getByPath, isValueChanged } from "../../../util/object-util";
import {
	fromRadioValue,
	isShown,
	toRadioValue,
	toSortedSections
} from "../../../util/form-schema-util";
import { declaresSetting } from "../../../util/ruleset-util";
import { IconMap } from "./IconMap";
import SectionDefinitions from "./SectionDefinitions";
import { entriesForSection } from "../../../util/definitions-util";

// Pure schema builders kept at module scope so the component can memoize
// them — rebuilding the ui schema and the full Yup schema on every render made
// the whole form feel sluggish.
export const buildUiSchema = (
	config,
	customFormData,
	rulesetDefaults,
	parent
) => {
	const uiSchema = {};

	Object.entries(config).forEach(([key, value]) => {
		const fullPropPath = parent ? `${parent}.${key}` : key;
		if (value instanceof Object && !Array.isArray(value)) {
			uiSchema[key] = {
				...buildUiSchema(value, customFormData, rulesetDefaults, fullPropPath),
				title: SportUtils.fromCamelCase(key),
				type: "object"
			};
		} else if (
			key !== "_type" &&
			// A setting a previous ruleset left behind stays in the stored
			// config - it still prices already-recorded plays - but it is not
			// a setting of the ruleset in force, so it is not offered here.
			declaresSetting(rulesetDefaults, parent, key)
		) {
			const formData = getByPath(customFormData, fullPropPath);

			uiSchema[key] = {
				key: fullPropPath,
				title: SportUtils.fromCamelCase(key),
				type:
					Array.isArray(value) || (formData && formData.options)
						? "array"
						: typeof value,
				row: 1,
				order: 1,
				value
			};

			Object.assign(uiSchema[key], formData);
		}
	});

	return uiSchema;
};

export const getInitialValues = (config) => {
	const values = {};
	Object.entries(config).forEach(([key, value]) => {
		if (value instanceof Object && !Array.isArray(value)) {
			Object.assign(values, { [key]: getInitialValues(value) });
		} else {
			Object.assign(values, { [key]: value });
		}
	});

	return values;
};

const getNumberValidation = (min, max) => {
	let numberFieldRule = Yup.number()
		.typeError("Invalid")
		.required("Required")
		.integer("Invalid");

	if (min) {
		numberFieldRule = numberFieldRule.moreThan(min - 1, "Invalid");
	}
	if (max) {
		numberFieldRule = numberFieldRule.max(max, `Max: ${max}`);
	}

	return numberFieldRule;
};

// formData is the form data at the same level as config, so a section recurses
// with its own slice. values are the live form values: a field its visibleWhen
// hides is not validated, since the crew cannot see or correct it.
export const getValidationSchema = (
	config,
	formData,
	values,
	customValidation
) => {
	const validationSchema = {};
	//
	Object.entries(config).forEach(([key, value]) => {
		if (value instanceof Object && !Array.isArray(value)) {
			Object.assign(validationSchema, {
				[key]: Yup.object(getValidationSchema(value, formData?.[key], values))
			});
		} else if (formData?.[key] && isShown(formData[key], values)) {
			const { min, max } = formData[key];
			if (min || max) {
				Object.assign(validationSchema, {
					[key]: getNumberValidation(min, max)
				});
			}
		}
	});
	//
	if (customValidation) {
		Object.assign(validationSchema, customValidation);
	}
	//
	return validationSchema;
};

const Configuration = forwardRef(
	(
		{
			configuration,
			customFormData,
			rulesetDefaults,
			onSaveConfig,
			disabled,
			preview,
			secondaryActions,
			customValidation,
			// Grouped setting definitions from core, and the name of the rule
			// set they describe. Resolved by the parent, which already knows
			// the sport and rule set; Configuration only matches them to the
			// section it is drawing.
			definitions,
			ruleSetName,
			// Sport-specific behavior plug-in (e.g. flag-football-configuration):
			// { componentRenderers?, interceptChange?, isFullWidth? }. Omitted
			// for sports with no custom form behavior.
			extension
		},
		ref
	) => {
		const commonClasses = commonStyles();
		const classes = configurationStyles({ preview });

		const getIconAdornment = (icon) => {
			return icon ? (
				<img className={classes.iconAdornment} src={IconMap[icon]} alt={icon} />
			) : null;
		};

		const uiSchema = useMemo(
			() => buildUiSchema(configuration, customFormData, rulesetDefaults),
			[configuration, customFormData, rulesetDefaults]
		);

		// Some fields (clock settings) stay editable while the form is
		// locked mid-game — Save/Cancel must stay usable for them too.
		const hasAlwaysEditableFields = useMemo(
			() =>
				Object.values(uiSchema).some(
					(section) =>
						section.type === "object" &&
						Object.values(section).some((field) => field && field.editableAfterStart)
				),
			[uiSchema]
		);
		const actionsDisabled = disabled && !hasAlwaysEditableFields;

		const validationSchema = useMemo(
			() =>
				Yup.lazy((values) =>
					Yup.object(
						getValidationSchema(
							configuration,
							customFormData,
							values,
							customValidation
						)
					)
				),
			[configuration, customFormData, customValidation]
		);

		const initialValues = useMemo(
			() => getInitialValues(configuration),
			[configuration]
		);

		const formik = useFormik({
			enableReinitialize: true,
			initialValues,
			validationSchema,
			onSubmit: (values) => {
				onSaveConfig(values);
			}
		});
		const {
			handleSubmit,
			handleChange,
			resetForm,
			setFieldValue,
			setValues,
			errors,
			isValid
		} = formik;

		useImperativeHandle(ref, () => ({
			reset() {
				resetForm();
			}
		}));

		// Minimal form surface handed to the sport extension: read values, write
		// one field, or batch-write the whole values object (one render + one
		// validation pass — per-field writes are unbatched on React 16).
		const formApi = {
			values: formik.values,
			setFieldValue,
			setValues,
			disabled,
			preview
		};

		// The extension may claim a change (coupled-value bookkeeping, e.g.
		// flag football sanitizing markers on field-length edits); unclaimed
		// changes take the plain formik path.
		const setValue = (field, value) => {
			if (!extension?.interceptChange?.(field, value, formApi)) {
				setFieldValue(field, value);
			}
		};

		const createElement = (element) => {
			const {
				key,
				title,
				min,
				max,
				type,
				options,
				multiselect,
				disabled: elementDisabled,
				editableAfterStart,
				icon,
				component
			} = element;
			// Definitions moved to one dialog per section (PSTATS-1267), so a
			// field's label is just its title again.
			const label = title;
			const inputProps =
				min || max
					? {
							min,
							max
					  }
					: {};
			// Fields flagged editableAfterStart (clock settings) ignore the
			// form-wide lock; the core save guard only lets their changes
			// through after game start.
			const fieldDisabled =
				(disabled && !editableAfterStart) || elementDisabled || preview;

			let returnValue = (
				<Typography variant="caption" key={key}>
					Unknown prop type {element.key}
				</Typography>
			);

			const customRenderer = extension?.componentRenderers?.[component];
			if (customRenderer) {
				returnValue = customRenderer(element, formApi);
			} else if (component === "radioGroup") {
				// Same 56px notched box the switch fields use, so a radio group
				// lines up with its neighbours instead of sitting proud of them.
				returnValue = (
					<fieldset key={key} className={classes.radioField}>
						<legend className={classes.switchFieldLabel}>{label}</legend>
						<RadioGroup
							row
							id={key}
							name={key}
							className={classes.radioFieldControl}
							value={toRadioValue(getByPath(formik.values, key))}
							onChange={(e) => setValue(key, fromRadioValue(options, e.target.value))}
						>
							{options.map((option) => (
								<FormControlLabel
									key={toRadioValue(option.code)}
									value={toRadioValue(option.code)}
									className={classes.radioFieldOption}
									control={
										<Radio
											color="primary"
											size="small"
											className={classes.radioFieldRadio}
										/>
									}
									label={option.name}
									disabled={fieldDisabled}
								/>
							))}
						</RadioGroup>
					</fieldset>
				);
			} else if (type === "boolean") {
				// Booleans without options render as a switch (booleans with
				// options come through as type "array" and render as a select).
				// The fieldset/legend mimic an outlined input so the control
				// lines up with the sibling fields, label in the notch.
				returnValue = (
					<fieldset key={key} className={classes.switchField}>
						<legend className={classes.switchFieldLabel}>{label}</legend>
						<Switch
							id={key}
							className={classes.switchFieldControl}
							checked={Boolean(getByPath(formik.values, key))}
							onChange={(e) => setValue(key, e.target.checked)}
							color="primary"
							disabled={fieldDisabled}
						/>
					</fieldset>
				);
			} else if (type === "number" || type === "string") {
				returnValue = (
					<TextField
						id={key}
						name={key}
						label={label}
						key={key}
						fullWidth
						type={type}
						value={getByPath(formik.values, key)}
						onChange={
							extension?.interceptChange
								? (e) => {
										// The extension may claim the change (e.g. field
										// length sanitizing); unclaimed changes take
										// formik's plain path.
										if (!extension.interceptChange(key, e.target.value, formApi)) {
											handleChange(e);
										}
								  }
								: handleChange
						}
						disabled={fieldDisabled}
						InputProps={{
							inputProps,
							endAdornment: getIconAdornment(icon)
						}}
						InputLabelProps={{ required: false }}
						helperText={getByPath(errors, key)}
						error={Boolean(getByPath(errors, key))}
						required
					/>
				);
			} else if (element.type === "array") {
				if (options) {
					if (multiselect) {
						returnValue = (
							<Autocomplete
								disabled={fieldDisabled}
								popupIcon={getIconAdornment(icon)}
								disableCloseOnSelect
								multiple
								limitTags={0}
								id={key}
								options={options}
								getOptionLabel={(option) => option.name}
								value={getByPath(formik.values, key).map((code) =>
									options.find((o) => o.code === code)
								)}
								onChange={(_, selections) => {
									setValue(
										key,
										selections.map(({ code }) => code)
									);
								}}
								renderOption={(option) => {
									return (
										<>
											<Checkbox
												checkedIcon={<CheckBoxIcon />}
												style={{ marginRight: 8 }}
												checked={getByPath(formik.values, key).some(
													(x) => x === option.code
												)}
											/>
											{option.name}
										</>
									);
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
										variant="outlined"
										label={label}
									/>
								)}
							/>
						);
					} else {
						returnValue = (
							<Autocomplete
								id={key}
								options={options}
								value={options.find((o) => o.code === getByPath(formik.values, key))}
								getOptionLabel={(option) => option.name}
								getOptionSelected={(option, value) => {
									return option.code === value.code;
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										className={classes.autoComplete}
										label={label}
										variant="outlined"
										InputProps={{
											...params.InputProps,
											endAdornment: getIconAdornment(icon)
										}}
									/>
								)}
								onChange={(e, option) => setValue(key, option.code)}
								disabled={fieldDisabled}
								disableClearable
							/>
						);
					}
				} else {
					returnValue = (
						<TextField
							id={key}
							name={key}
							label={label}
							key={key}
							fullWidth
							type={type}
							value={getByPath(formik.values, key)}
							onChange={handleChange}
							disabled
							InputProps={{
								inputProps,
								endAdornment: getIconAdornment(icon)
							}}
							helperText={getByPath(errors, key)}
							error={Boolean(getByPath(errors, key))}
						/>
					);
				}
			}

			return returnValue;
		};

		// Two labelled radios need about 230px. With the sidebar open a quarter
		// column measures 230px at a 1366px window and 272px at 1536px, so the
		// crossover sits near 1400px - a width no MUI breakpoint expresses,
		// hence the media query. Above it a radio group takes one column like
		// every other field; below it two of the four, and one of the two on a
		// small tablet, so its options stay on one line, its edges land on the
		// column grid, and it never sits alone above a fuller row. The preview
		// panel is a dialog narrower than the viewport, so there it takes half
		// of the two-per-row layout.
		const wideColumns = useMediaQuery("(min-width:1400px)");
		let radioGroupColumns = { sm: 6, md: wideColumns ? 3 : 6 };
		if (preview) {
			radioGroupColumns = { sm: 6, md: 6 };
		}

		const createRow = (row) =>
			row.map((r) => {
				const fullWidth = Boolean(extension?.isFullWidth?.(r, formApi));
				const defaultMd = preview ? 6 : 3;
				let columns = { sm: 6, md: defaultMd };
				if (fullWidth) {
					columns = { sm: 12, md: 12 };
				} else if (r.component === "radioGroup") {
					columns = radioGroupColumns;
				}
				return (
					<Grid
						item
						xs={12}
						{...columns}
						key={r.key}
						className={
							isValueChanged(formik.values, formik.initialValues, r.key)
								? commonClasses.changedField
								: undefined
						}
					>
						{createElement(r)}
					</Grid>
				);
			});

		const createSection = (section) => {
			return (
				<Box className={classes.section} key={section.title}>
					<Divider className={classes.sectionDivider} />
					<Box className={classes.sectionHeader}>
						<Typography variant="h5" className={classes.sectionLabel}>
							{section.title}
						</Typography>
						<SectionDefinitions
							sectionTitle={section.title}
							ruleSetName={ruleSetName}
							entries={entriesForSection(definitions, section.key)}
						/>
					</Box>
					<Typography variant="body1">{section.description}</Typography>
					<Grid
						container
						spacing={4}
						direction="row"
						justifyContent="flex-start"
						alignItems="flex-start"
						className={classes.sectionRow}
					>
						{Object.values(section.rows).flatMap((row, index) => [
							// Form-data rows are rows: a full-width, zero-height
							// child forces the wrap, so a later row never packs
							// into the space an earlier one leaves free.
							index > 0 ? (
								<div key={`row-break-${index}`} className={classes.rowBreak} />
							) : null,
							...createRow(row)
						])}
					</Grid>
				</Box>
			);
		};

		const buildUi = (schema) => {
			// Drawn against the values being edited, so a field whose form data
			// declares visibleWhen appears and disappears as they change
			// (PSTATS-1269, the list form PSTATS-1268). Its value stays in formik
			// and is saved either way; getValidationSchema skips it while hidden.
			const sections = toSortedSections(
				schema,
				customFormData?.sectionOrder,
				formik.values
			);
			return <div>{sections.map(createSection)}</div>;
		};

		return uiSchema ? (
			<form className={commonClasses.form} onSubmit={handleSubmit}>
				{buildUi(uiSchema)}
				{!preview && (
					<Toolbar className={commonClasses.actionToolbar}>
						<Box className={classes.actionContainer}>
							{secondaryActions}
							<Box className={classes.primaryActionContainer}>
								{formik.dirty && (
									<Typography
										variant="body2"
										role="status"
										className={commonClasses.unsavedChanges}
									>
										Unsaved changes
									</Typography>
								)}
								<SecondaryButton
									color="secondary"
									label="Cancel"
									onClick={resetForm}
									disabled={actionsDisabled}
								/>
								<PrimaryButton
									type="submit"
									color="primary"
									label="Save"
									disabled={actionsDisabled || !isValid}
								/>
							</Box>
						</Box>
					</Toolbar>
				)}
			</form>
		) : null;
	}
);

export default Configuration;
